import React, { useEffect, useMemo, useState } from "react";
import { apiPut } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Pencil, CheckCircle2, ScanLine, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * IpoFinancialsVerifyDialog
 * -------------------------
 * Admin-only dialog that lets the operator overwrite any extracted
 * financial figure on an IPO analysis. Calls
 *   PUT /api/admin/ipo/{symbol}/financials
 * with a year-by-year merge payload. The backend tags every touched
 * field with `admin_verified` source so the composite scorer picks
 * admin values over Vision-extracted ones.
 *
 * Props:
 *   open, onOpenChange  — controlled dialog state
 *   ipo                 — full IPO analysis object (for symbol + seed values)
 *   onSaved             — callback(updatedFinancials)
 */
const YEAR_ROWS = [
  { key: "revenue",    label: "Revenue (Rs.)",    scale: 1e6, suffix: " Mn" },
  { key: "net_profit", label: "Net profit (Rs.)", scale: 1e6, suffix: " Mn" },
  { key: "eps",        label: "EPS (Rs.)",        scale: 1,   suffix: "" },
  { key: "bvps",       label: "BVPS (Rs.)",       scale: 1,   suffix: "" },
  { key: "roe",        label: "ROE (%)",          scale: 1,   suffix: "" },
  { key: "dps",        label: "DPS (Rs.)",        scale: 1,   suffix: "" },
];
const SCALAR_ROWS = [
  { key: "debt_to_equity",   label: "Debt / Equity" },
  { key: "current_ratio",    label: "Current Ratio" },
  { key: "revenue_cagr_5yr", label: "Revenue 5Y CAGR (%)" },
  { key: "eps_cagr_5yr",     label: "EPS 5Y CAGR (%)" },
];

const formatSeed = (v, scale, decimals) => {
  if (v == null) return "";
  const scaled = v / scale;
  const d = decimals != null ? decimals : (scale >= 1e6 ? 0 : 2);
  return Number.isFinite(scaled) ? String(Number(scaled.toFixed(d))) : "";
};

const IpoFinancialsVerifyDialog = ({ open, onOpenChange, ipo, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const symbol = ipo?.symbol;
  const fin = ipo?.financials || {};

  // Gather the union of all years present across every per-year map so
  // admin can add data for a year that previously had nothing.
  const years = useMemo(() => {
    const s = new Set();
    for (const r of YEAR_ROWS) {
      Object.keys(fin[r.key] || {}).forEach((y) => s.add(y));
    }
    const list = Array.from(s).sort();
    // Ensure we always have 5 slots so admin can fill blank years.
    const last = list.length ? Number(list[list.length - 1]) : new Date().getFullYear();
    while (list.length < 5) {
      list.unshift(String((last - list.length)));
    }
    return list;
  }, [fin]);

  const visionExtracted = !!(ipo?.pipeline?.vision_extracted);
  const alreadyVerified = !!(ipo?.pipeline?.admin_verified_at);

  useEffect(() => {
    if (!open) return;
    const seed = {};
    for (const r of YEAR_ROWS) {
      for (const y of years) {
        seed[`${r.key}::${y}`] = formatSeed((fin[r.key] || {})[y], r.scale);
      }
    }
    for (const r of SCALAR_ROWS) {
      seed[`scalar::${r.key}`] = fin[r.key] == null ? "" : String(fin[r.key]);
    }
    setValues(seed);
    setNotes(fin.admin_verification_notes || "");
  }, [open, years, fin]);

  const onChange = (key) => (e) => setValues((s) => ({ ...s, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!symbol) return;
    setSaving(true);
    // Build payload: for each year-key, send a dict {year: val}; only
    // include entries where admin provided a number (empty means no change
    // → we skip to avoid wiping the year). Explicit "clear" is sending
    // `null` in the API, but the UI treats blank as "leave alone".
    const payload = {};
    for (const r of YEAR_ROWS) {
      const perYear = {};
      for (const y of years) {
        const raw = (values[`${r.key}::${y}`] || "").trim();
        if (raw === "") continue;
        const n = Number(raw);
        if (!Number.isFinite(n)) {
          toast.error(`"${r.label}" ${y} must be a number`);
          setSaving(false);
          return;
        }
        perYear[y] = n * (r.scale || 1);
      }
      if (Object.keys(perYear).length) payload[r.key] = perYear;
    }
    for (const r of SCALAR_ROWS) {
      const raw = (values[`scalar::${r.key}`] || "").trim();
      if (raw === "") continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        toast.error(`"${r.label}" must be a number`);
        setSaving(false);
        return;
      }
      payload[r.key] = n;
    }
    if (notes && notes.trim()) payload.notes = notes.trim();
    if (!Object.keys(payload).length) {
      toast.info("Nothing to save — fill at least one field.");
      setSaving(false);
      return;
    }
    try {
      const res = await apiPut(`/admin/ipo/${encodeURIComponent(symbol)}/financials`, payload);
      toast.success("Data verified and saved.", {
        description: `Admin overrides will be preferred over Vision / text extraction on future re-runs.`,
      });
      onOpenChange(false);
      onSaved?.(res?.financials || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="ipo-financials-verify-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Pencil className="h-4 w-4 text-primary" />
            Verify Extracted Data — {ipo?.company_name || symbol}
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center gap-2 flex-wrap">
              <span>
                Please verify these figures against the original PDF. Saved
                values take priority over Vision / text extraction.
              </span>
              {visionExtracted && (
                <Badge
                  variant="outline"
                  className="gap-1 text-[10px] border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
                >
                  <ScanLine className="h-3 w-3" />
                  AI Vision source
                </Badge>
              )}
              {alreadyVerified && (
                <Badge
                  variant="outline"
                  className="gap-1 text-[10px] border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Previously verified
                </Badge>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-5">
          {/* Per-year matrix */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="ipo-verify-table">
              <thead>
                <tr className="border-b text-[10px] uppercase text-muted-foreground">
                  <th className="text-left py-2 pr-2">Metric</th>
                  {years.map((y) => (
                    <th key={y} className="text-right py-2 px-1 font-mono">{y}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {YEAR_ROWS.map((r) => (
                  <tr key={r.key} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 text-muted-foreground whitespace-nowrap">
                      {r.label}
                    </td>
                    {years.map((y) => (
                      <td key={y} className="px-1 py-1.5">
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="any"
                          value={values[`${r.key}::${y}`] ?? ""}
                          onChange={onChange(`${r.key}::${y}`)}
                          placeholder="—"
                          className="h-8 text-right tabular font-mono text-xs"
                          data-testid={`ipo-verify-${r.key}-${y}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Scalar ratios */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
              Key Ratios
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {SCALAR_ROWS.map((r) => (
                <div key={r.key} className="space-y-1">
                  <Label htmlFor={`scalar-${r.key}`} className="text-[11px]">
                    {r.label}
                  </Label>
                  <Input
                    id={`scalar-${r.key}`}
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={values[`scalar::${r.key}`] ?? ""}
                    onChange={onChange(`scalar::${r.key}`)}
                    placeholder="—"
                    className="h-9 tabular"
                    data-testid={`ipo-verify-scalar-${r.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Admin note */}
          <div>
            <Label htmlFor="admin-notes" className="text-[11px]">
              Admin note (optional)
            </Label>
            <Textarea
              id="admin-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="E.g. Revenue 2021-2023 corrected against page 42 of the consolidated accountants' report."
              className="mt-1 text-xs"
              data-testid="ipo-verify-notes"
            />
          </div>

          <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5 px-3 py-2 text-[11px] text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--info))]" />
            <div>
              Leave a cell blank to keep the current value. Saving marks every
              edited field with <span className="font-mono">admin_verified</span> so it
              takes priority over any future Vision or text-extraction re-run.
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="ipo-verify-cancel"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2" data-testid="ipo-verify-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Verified Data
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default IpoFinancialsVerifyDialog;
